import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import setAuthToken from '../../utils/setAuthToken';
import { axiosPrivate } from '../hooks/axiosInstance';

export const signup = createAsyncThunk(
  'auth/signup',
  async (credentials, thunkAPI) => {
    try {
      const response = await axiosPrivate.post(`/admin/register`, credentials);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, thunkAPI) => {
    try {
      // If no credentials provided, this is an auto-login attempt (checking existing token)
      if (!credentials) {
        const token = Cookies.get('token');
        if (!token) {
          throw new Error('No token found');
        }
        // Verify the existing token by getting user info
        setAuthToken(token);
        const response = await axiosPrivate.get('/admin/user');
        return { ...response.data, token };
      } else {
        // Regular login with credentials
        const response = await axiosPrivate.post(`/admin/auth`, credentials);
        return response.data;
      }
    } catch (error) {
      // Clear invalid token
      Cookies.remove('token');
      setAuthToken(null);
      return thunkAPI.rejectWithValue(error.response?.data || { msg: 'Authentication failed' });
    }
  }
);

export const getUser = createAsyncThunk(
  'user/getUser',
  async (credentials, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/admin/user`, credentials);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const clearError = createAction('auth/clearError');

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: Cookies.get('token') || null,
    loading: false,                      // Changed: Start with loading false
    isInitialized: true,                 // Changed: Start initialized
    success: false,
    error: null,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isInitialized = true;
    },
    clearUser: (state) => {
      state.user = null;
      state.token = null;
      state.isInitialized = true;
      state.loading = false;
      Cookies.remove('token');
      setAuthToken(null);
    },
    initializeAuth: (state) => {
      state.loading = false;
      state.isInitialized = true;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(signup.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(signup.fulfilled, (state, action) => {
      state.loading = false;
      state.isInitialized = true;
      state.user = action.payload;
      state.token = action.payload.token;
      setAuthToken(action.payload.token);
      Cookies.set('token', action.payload.token, { expires: 7 });
    })
    .addCase(signup.rejected, (state, action) => {
      state.loading = false;
      state.isInitialized = true;
      state.error = action.payload ? action.payload.msg : action.error.message;
    })
    .addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.isInitialized = true;
      state.user = action.payload;
      state.token = action.payload.token;
      setAuthToken(action.payload.token);
      
      // Save token to cookie if it's a new login (has credentials)
      if (action.payload.token) {
        Cookies.set('token', action.payload.token, { expires: 7 });
      }
    })
    .addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.isInitialized = true;
      state.user = null;
      state.token = null;
      state.error = action.payload?.msg || 'Login failed';
      Cookies.remove('token');
      setAuthToken(null);
    })
    .addCase(clearError, (state) => {
      state.error = null;
    })
    // getUser
    .addCase(getUser.pending, (state) => {
      state.loading = true;
    })
    .addCase(getUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = false;
      state.error = null;
      state.success = true;
      state.isInitialized = true;
    })
    .addCase(getUser.rejected, (state, action) => {
      state.success = false;
      state.loading = false;
      state.isInitialized = true;
      state.error = action.payload;
      state.user = null;
      state.token = null;
      Cookies.remove('token');
      setAuthToken(null);
    });
  },
});

export const { setUser, clearUser, initializeAuth } = authSlice.actions;

export default authSlice.reducer;